/**
 * Label anchor points for the deals map: county names, township-range labels
 * ("7N-7W") and section numbers. Pure — returns GeoJSON Point
 * FeatureCollections for symbol layers. No React, no network.
 *
 * Mapbox's own symbol placement on a polygon source repeats the label per
 * tile and drifts as you pan, so each label gets one explicit point instead.
 */
import { canonicalTrs } from "./aliquot";
import { normalizeCounty } from "./trsAggregation";

const EMPTY = () => ({ type: "FeatureCollection", features: [] });

// Signed area and centroid of a ring (shoelace).
const ringCentroid = (ring) => {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x0, y0] = ring[j];
    const [x1, y1] = ring[i];
    const f = x0 * y1 - x1 * y0;
    a += f;
    cx += (x0 + x1) * f;
    cy += (y0 + y1) * f;
  }
  a /= 2;
  if (Math.abs(a) < 1e-15) return null;
  return { area: Math.abs(a), point: [cx / (6 * a), cy / (6 * a)] };
};

const bboxCenter = (ring) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return Number.isFinite(minX) ? [(minX + maxX) / 2, (minY + maxY) / 2] : null;
};

/**
 * One label point for a Polygon / MultiPolygon: the area centroid of the
 * largest outer ring (falls back to that ring's bbox centre when degenerate).
 *
 * @returns {[number, number]|null}
 */
export const polygonLabelPoint = (geometry) => {
  if (!geometry) return null;
  let rings = [];
  if (geometry.type === "Polygon") rings = [geometry.coordinates?.[0]];
  else if (geometry.type === "MultiPolygon") {
    rings = (geometry.coordinates || []).map((p) => p?.[0]);
  }
  let best = null;
  let fallback = null;
  for (const ring of rings) {
    if (!Array.isArray(ring) || ring.length < 3) continue;
    if (!fallback) fallback = bboxCenter(ring);
    const c = ringCentroid(ring);
    if (c && (!best || c.area > best.area)) best = c;
  }
  return best ? best.point : fallback;
};

const point = (coordinates, properties) => ({
  type: "Feature",
  geometry: { type: "Point", coordinates },
  properties,
});

/**
 * County name points. `selected` is true on the emphasised counties (a name
 * or a list of names, compared through `normalizeCounty`) so the layer can
 * draw them larger — the deals map emphasises the counties holding tracts.
 */
export const countyLabelPoints = (countiesFC, emphasised) => {
  const out = EMPTY();
  const list = Array.isArray(emphasised) ? emphasised : emphasised ? [emphasised] : [];
  const want = new Set(list.map((c) => normalizeCounty(c)).filter(Boolean));
  for (const f of Array.isArray(countiesFC?.features) ? countiesFC.features : []) {
    const name = f?.properties?.county;
    const p = polygonLabelPoint(f?.geometry);
    if (!name || !p) continue;
    out.features.push(
      point(p, { name: String(name), selected: want.has(normalizeCounty(name)) })
    );
  }
  return out;
};

/**
 * One "7N-7W" point per township, at the mean of its sections' label points
 * (derived from the loaded section polygons, props twp / rng / sec).
 */
export const townshipLabelPoints = (sectionsFC) => {
  const acc = new Map();
  for (const f of Array.isArray(sectionsFC?.features) ? sectionsFC.features : []) {
    const pr = f?.properties || {};
    const key = canonicalTrs(pr.twp, pr.rng, pr.sec);
    const p = polygonLabelPoint(f?.geometry);
    if (!key || !p) continue;
    const tr = key.split("-").slice(0, 2).join("-");
    const a = acc.get(tr) || { x: 0, y: 0, n: 0 };
    a.x += p[0];
    a.y += p[1];
    a.n += 1;
    acc.set(tr, a);
  }
  const out = EMPTY();
  for (const [label, a] of acc.entries()) {
    out.features.push(point([a.x / a.n, a.y / a.n], { label }));
  }
  return out;
};

/** One section-number point per section ("21", padding dropped). */
export const sectionLabelPoints = (sectionsFC) => {
  const out = EMPTY();
  for (const f of Array.isArray(sectionsFC?.features) ? sectionsFC.features : []) {
    const pr = f?.properties || {};
    const key = canonicalTrs(pr.twp, pr.rng, pr.sec);
    const p = polygonLabelPoint(f?.geometry);
    if (!key || !p) continue;
    out.features.push(point(p, { label: key.split("-")[2] }));
  }
  return out;
};
